import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';
import ButtonsMain from '../Button/ButtonMain';
import { Rocket, Target, HelpCircle } from 'lucide-react';
import { useTranslation } from '../../language';
import { useAuth } from '../../provider';
import { TourContext, TOUR_KEY, useTour } from './useTour';

/* ─────────────────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────────────────── */

const ROUTES = {
  cockpit:      '/CockpitWelcome',
  mechanismen:  '/MechanismsWelcome',
  empfehlungen: '/RecommendationsWelcome',
  arzt:         '/DoctorNotesWelcome',
  laborwerte:   '/BloodtestsWelcome',
  bioage:       '/BioAgeWelcome',
};

const NAV_BEFORE_STEP = {
  2: { route: ROUTES.mechanismen,  target: '[data-tour="saeulen-cards"]' },
  4: { route: ROUTES.empfehlungen, target: '[data-tour="empfehlung-top"]' },
  5: { route: ROUTES.arzt,         target: '[data-tour="arzt-sprechen"]' },
  7: { route: ROUTES.laborwerte,   target: '[data-tour="laborwerte-marker"]' },
  8: { route: ROUTES.bioage,       target: '[data-tour="bioage-chart"]' },
};

// The page a step lives on — the route of the closest NAV_BEFORE_STEP entry
// at or before it, or the cockpit for the steps ahead of the first one.
function routeForStep(idx) {
  for (let i = idx; i >= 0; i--) {
    if (NAV_BEFORE_STEP[i]) return NAV_BEFORE_STEP[i].route;
  }
  return ROUTES.cockpit;
}

// Real height of the fixed AppBar (MUI's <AppBar> renders as a <header> by
// default), measured live instead of guessed — steps without their own
// explicit scrollHeaderOffset use this as the true minimum clearance a
// target needs from the top of the viewport, rather than a static number
// that might be too small (clipping the target behind the header) or too
// big (wasting space a tall target's tooltip could have used).
function getHeaderOffset(buffer = 12, fallback = 90) {
  const height = document.querySelector('header')?.getBoundingClientRect().height;
  return height > 0 ? height + buffer : fallback;
}

// Joyride's own built-in scroll-into-view silently fails in this app (its
// animated-scroll call never actually moves the page). options.skipScroll
// below disables it entirely and every step advance scrolls manually here
// instead. Deliberately instant, not { behavior: 'smooth' } — smooth/animated
// scrolling is what silently fails here (both Joyride's own attempt and a
// plain window.scrollTo({behavior:'smooth'}) do nothing; an instant jump
// always works). headerOffset matches the fixed AppBar height.
// `centered`: on xs screens, pin the target flush under the header (like
// desktop) biases ALL remaining vertical space downward for the tooltip —
// if the target is tall, the tooltip has nowhere to go without the user
// scrolling. Centering the target in the viewport instead leaves roughly
// equal room above and below for Joyride/floating-ui to place the tooltip
// on whichever side it lands, without pushing the target itself up behind
// the fixed header (clamped via maxScrollTop below).
function scrollTargetIntoView(selector, headerOffset = 90, centered = false) {
  const el = document.querySelector(selector);
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const absoluteTop = rect.top + window.scrollY;
  const maxScrollTop = absoluteTop - headerOffset;

  if (!centered) {
    window.scrollTo(0, Math.max(0, maxScrollTop));
    return;
  }

  const targetCenter = absoluteTop + rect.height / 2;
  const centeredScrollTop = targetCenter - window.innerHeight / 2;
  window.scrollTo(0, Math.max(0, Math.min(centeredScrollTop, maxScrollTop)));
}

// scrollTargetIntoView positions things before Joyride has actually rendered
// its tooltip, so it can only guess how tall the tooltip will be. This runs
// afterward, once the tooltip is a real element in the DOM (react-joyride's
// floater carries a stable `.react-joyride__floater` class), and nudges the
// scroll position so BOTH the target and the tooltip actually fit on
// screen — not just the target. Only ever scrolls further, and never far
// enough to push the target's own top back up behind the fixed header.
function fitStepAndTooltipInViewport(targetSelector, headerOffset = 90) {
  let attempts = 0;
  const tryFit = () => {
    const el = document.querySelector(targetSelector);
    const floater = document.querySelector('.react-joyride__floater');
    if (!el || !floater) {
      if (attempts++ < 10) requestAnimationFrame(tryFit);
      return;
    }

    const targetRect = el.getBoundingClientRect();
    const floaterRect = floater.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const blockBottom = Math.max(targetRect.bottom, floaterRect.bottom);

    let delta = 0;
    if (blockBottom > viewportHeight) {
      delta = blockBottom - viewportHeight;
    }
    // Never scroll down so far the target's own top ends up above headerOffset.
    const maxDelta = targetRect.top - headerOffset;
    delta = Math.min(delta, Math.max(maxDelta, 0));

    if (delta > 1) {
      window.scrollTo(0, Math.max(0, window.scrollY + delta));
    }
  };
  requestAnimationFrame(tryFit);
}

/* ─────────────────────────────────────────────────────────────────────────────
   TOUR CONTEXT
   Context holds only a ref to WalkthroughTour's restart handler.
   All Joyride state lives locally in WalkthroughTour — same component that
   renders <Joyride> — which is the only architecture that works reliably.
───────────────────────────────────────────────────────────────────────────── */

export function TourProvider({ children }) {
  const restartRef = useRef(null);
  return (
    <TourContext.Provider value={restartRef}>
      {children}
    </TourContext.Provider>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   STEP CONTENT HELPERS
───────────────────────────────────────────────────────────────────────────── */

function Highlight({ text, type }) {
  const theme = useTheme();
  const p = theme.palette;
  const color = type === 'amber' ? p.traffic.orange
              : type === 'accent' ? p.quartiary.C500
              : p.traffic.red;
  return (
    <Box sx={{
      borderLeft: `4px solid ${color}`,
      bgcolor: alpha(color, 0.07),
      px: 1.5, py: 1,
      borderRadius: '0 6px 6px 0',
      mt: 1.25, mb: 0.5,
    }}>
      <Typography variant="body2" fontStyle="italic" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {text}
      </Typography>
    </Box>
  );
}

function GoalBox({ text }) {
  const theme = useTheme();
  const color = theme.palette.traffic.orange;
  return (
    <Box sx={{
      bgcolor: alpha(color, 0.07),
      border: `1px solid ${alpha(color, 0.4)}`,
      borderRadius: 2,
      px: 1.75, py: 1.25, mt: 1.5,
      display: 'flex', alignItems: 'flex-start', gap: 1,
    }}>
      <Target size={16} color={color} style={{ marginTop: 2, flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {text}
      </Typography>
    </Box>
  );
}

function AutoTriggerBeacon() {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.closest('button')?.click();
  }, []);
  return <span ref={ref} />;
}

function StepText({ children }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, m: 0 }}>
      {children}
    </Typography>
  );
}

function StepSectionList({ items }) {
  const theme = useTheme();
  const accents = [
    theme.palette.tertiary.C300,
    theme.palette.quartiary.C300,
    theme.palette.tertiary.C300,
    theme.palette.quartiary.C300,
  ];
  return (
    <Box sx={{ mt: 1.5 }}>
      {items.map((item, i) => (
        <Box key={i} sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          py: 0.9,
          ...(i < items.length - 1 ? { borderBottom: '1px solid', borderColor: 'divider' } : {}),
        }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: accents[i], flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   STEPS BUILDER
───────────────────────────────────────────────────────────────────────────── */

function buildSteps(t, isMobile, isMale) {
  const steps = [
    {
      target: '[data-tour="sidebar-toggle"]',
      skipBeacon: true,
      placement: 'bottom',
      title: t('tour_step_nav_title'),
      content: <StepText>{t('tour_step_nav_body')}</StepText>,
    },
    {
      // On mobile the drawer spans the full width (no room for 'right'), and the
      // full list is too tall to anchor 'bottom' against directly — use a small
      // fixed anchor partway down instead, arrow up, while still spotlighting
      // the whole list so the menu stays visible/highlighted behind it.
      target: isMobile ? '[data-tour="sidebar-list-anchor"]' : '[data-tour="sidebar-list"]',
      spotlightTarget: '[data-tour="sidebar-list"]',
      placement: isMobile ? 'bottom' : 'right',
      skipBeacon: true,
      title: t('tour_step_sections_title'),
      content: (
        <>
          <StepText>{t('tour_step_sections_body')}</StepText>
          <StepSectionList items={[
            t('tour_step_sections_item1'),
            t('tour_step_sections_item2'),
            t('tour_step_sections_item3'),
            t('tour_step_sections_item4'),
          ]} />
        </>
      ),
    },
    {
      // The card holding all pillars + mechanisms on the cockpit page.
      target: '[data-tour="saeulen-cards"]',
      skipBeacon: true,
      // The card with all 12 mechanism rows is taller than the viewport, so
      // the tooltip can't go below it. Same pattern as the arzt-sprechen step:
      // tooltip above the card, arrow down, and a large scrollHeaderOffset so
      // the room above the card isn't scrolled away — the card then runs on
      // below the tooltip with its mechanism rows visible.
      placement: 'top',
      scrollHeaderOffset: 420,
      title: t('tour_step0_title'),
      content: <StepText>{t('tour_step0_body')}</StepText>,
    },
    {
      target: '[data-tour="punkte-row"]',
      skipBeacon: true,
      title: t('tour_step1_title'),
      content: <StepText>{t('tour_step1_body')}</StepText>,
    },
    {
      target: '[data-tour="empfehlung-top"]',
      skipBeacon: true,
      title: t('tour_step2_title'),
      content: (
        <>
          <Highlight text={t('tour_step2_highlight')} type="red" />
          <StepText>{t('tour_step2_body')}</StepText>
        </>
      ),
    },
    {
      // The panel sits on its own page (/DoctorNotesWelcome). Anchor directly
      // to it with the tooltip above it, arrow down — reads as "this is about
      // the highlighted thing below". scrollHeaderOffset (read by
      // handleCallback, not a Joyride option) keeps the page from scrolling
      // the room above the panel away, so the tooltip has space there.
      target: '[data-tour="arzt-sprechen"]',
      placement: 'top',
      scrollHeaderOffset: 420,
      skipBeacon: true,
      title: t('tour_step3_title'),
      content: <StepText>{t('tour_step3_body')}</StepText>,
    },
    {
      target: '[data-tour="nav-addons"]',
      skipBeacon: true,
      placement: 'right',
      title: t('tour_step5_title'),
      content: (
        <>
          <StepText>{t('tour_step5_body')}</StepText>
          {/* Cycle-tracker advice only makes sense for users who have one — skip
              it entirely for male users instead of showing an irrelevant tip. */}
          {!isMale && <Highlight text={t('tour_step5_highlight')} type="accent" />}
        </>
      ),
    },
    {
      target: '[data-tour="laborwerte-marker"]',
      skipBeacon: true,
      title: t('tour_step7_title'),
      content: <StepText>{t('tour_step7_body')}</StepText>,
    },
    {
      target: '[data-tour="bioage-chart"]',
      skipBeacon: true,
      // This step's tooltip is unusually tall (body + highlight + goal box).
      // No static scrollHeaderOffset here — the actual fixed header height is
      // measured at scroll time instead (see getHeaderOffset), so the target
      // sits exactly as high as it safely can without guessing a number that
      // might clip it behind the real header.
      placement: 'bottom',
      title: t('tour_step8_title'),
      content: (
        <>
          <StepText>{t('tour_step8_body')}</StepText>
          <Highlight text={t('tour_step8_highlight')} type="amber" />
          {/* Drop the goal box on xs — with the tooltip already unusually
              tall on this step, this is the piece that pushes it past what
              fits on a mobile screen. */}
          {!isMobile && <GoalBox text={t('tour_step8_goal')} />}
        </>
      ),
    },
  ];

  if (!isMobile) return steps;

  // From step 2 onward (the first step with a Back button), pull Back out of the
  // footer row and badge it bottom-left, leaving Next alone at bottom-right
  // where the footer already puts it.
  const mobileFooterStyles = {
    tooltipTitle: { marginTop: 22 },
    // bottom: 26 matches the box's own bottom padding, so this sits level
    // with Next/Weiter, which ends exactly at that padding boundary.
    buttonBack: { position: 'absolute', bottom: 26, left: 14, marginLeft: 0 },
  };
  return steps.map((step, i) => (i === 0 ? step : { ...step, styles: mobileFooterStyles }));
}

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL HELPERS
───────────────────────────────────────────────────────────────────────────── */

function ModalOverlay({ children, maxWidth = 480 }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 10000,
      bgcolor: alpha(theme.palette.common.black, 0.65),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 3,
    }}>
      <Box sx={{
        // background.paper defaults to MUI's stock dark value (#121212) —
        // darker than the page's background.default, so the modal sank into
        // the dimmed backdrop instead of reading as a raised surface.
        bgcolor: dark ? 'greydark.C800' : 'background.paper',
        borderRadius: 4,
        p: { xs: '28px 20px', sm: '40px 36px 32px' },
        width: '100%', maxWidth,
        boxShadow: dark
          ? `0 24px 56px ${alpha(theme.palette.common.black, 0.6)}`
          : `0 24px 56px ${alpha(theme.palette.common.black, 0.18)}`,
      }}>
        {children}
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   CLOSING MODAL
───────────────────────────────────────────────────────────────────────────── */

function ClosingModal({ onFinish }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accentColor = theme.palette.tertiary.C700;
  const dark = theme.palette.mode === 'dark';

  return (
    <ModalOverlay maxWidth={500}>
      <Box sx={{ textAlign: 'center', mb: 2.75 }}>
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Rocket size={44} color={accentColor} />
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, lineHeight: 1.3 }}>
          {t('tour_closing_title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {t('tour_closing_body')}
        </Typography>
      </Box>

      <Box sx={{
        border: '1px solid',
        borderRadius: 3,
        px: 2.5, py: 2, mb: 2,
        ...(dark
          ? { bgcolor: 'greydark.C900', borderColor: 'greydark.C400' }
          : { bgcolor: 'grey.C50',      borderColor: 'grey.C900' }),
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 1.5 }}>
          {t('tour_closing_brand1')}
        </Typography>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
          <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.65 }}>
            {t('tour_closing_brand2')}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', mb: 2.25 }}>
        {t('tour_closing_hint')}
      </Typography>

      <ButtonsMain name={t('tour_closing_cta')} action={onFinish} sx={{ width: '100%' }} />
    </ModalOverlay>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   All Joyride-controlled state is LOCAL here — never in a context provider.
   This matches every official Joyride v3 example and avoids cross-component
   setState timing issues that break step advancement.
───────────────────────────────────────────────────────────────────────────── */

export default function WalkthroughTour() {
  const navigate   = useNavigate();
  const { t }      = useTranslation();
  const theme      = useTheme();
  const restartRef = useContext(TourContext);
  const { state: authState } = useAuth();
  const isMale     = authState.user?.gender === 'M';

  const p    = theme.palette;
  const dark = p.mode === 'dark';
  const accent = p.tertiary.C700;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Local state (same component as <Joyride>) ──────────────────────────────
  const [run,         setRun]         = useState(false);
  const [stepIndex,   setStepIndex]   = useState(0);
  const [showClosing, setShowClosing] = useState(false);
  // tourKey forces a full Joyride remount on restart, resetting its internal
  // state machine (FINISHED/SKIPPED are terminal in v3; a new key gives a
  // fresh store starting from STATUS.READY so run=true reliably starts it).
  const [tourKey,     setTourKey]     = useState(0);

  // Stable steps array — rebuilt only when the translation function changes
  const steps = useMemo(() => buildSteps(t, isMobile, isMale), [t, isMobile, isMale]);

  // Whether the tour is "in session" at all — true from the moment the first
  // step is shown, through every step-to-step transition (page navigation,
  // sidebar open/close polling, all of which briefly set run=false), all the
  // way to the closing modal. Only clears when the tour actually ends
  // (finished, skipped) or gets reset for a restart. This is deliberately
  // broader than `run` itself: `run` alone would unlock scrolling during
  // those transitional gaps, letting the background — whatever isn't the
  // thing currently being shown — scroll around underneath.
  const [tourActive, setTourActive] = useState(false);
  useEffect(() => {
    if (run) setTourActive(true);
  }, [run]);

  // Lock page scrolling for the whole tour session, not just individual steps
  // (see tourActive above) — the user can't scroll the background out of view
  // at any point during the tour. `overflow: hidden` only blocks user-driven
  // scrolling (wheel/touch/keyboard); it does not block scrollTargetIntoView's
  // own window.scrollTo calls, so step-to-step repositioning still works.
  useEffect(() => {
    if (!tourActive && !showClosing) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyTouchAction = body.style.touchAction;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.touchAction = prevBodyTouchAction;
    };
  }, [tourActive, showClosing]);

  // Tells page components which step's target is currently spotlighted (see
  // useIsTourStepActive in useTour.js), so they can render a collapsed/
  // shortened version of themselves for the duration of that one step — the
  // fix for a section whose full content is simply too tall to fit an xs
  // viewport together with the tooltip, however it's scrolled/positioned.
  useEffect(() => {
    const target = run ? steps[stepIndex]?.target : null;
    window.dispatchEvent(new CustomEvent('tour:step', { detail: { target } }));
  }, [run, stepIndex, steps]);

  // Register the restart handler so the WelcomePage button can call it
  useEffect(() => {
    restartRef.current = () => {
      // "Tour starten" can be clicked from inside the sidebar itself (scrolled
      // down to find it) — always force a clean baseline first: drawer closed,
      // page scrolled to top, regardless of where/how the tour was started.
      window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: { open: false } }));
      window.scrollTo(0, 0);
      setRun(false);
      setTourActive(false);
      setStepIndex(0);
      setShowClosing(false);
      // Increment key to remount <Joyride> with a fresh internal store.
      // This is required because v3's FINISHED/SKIPPED statuses are terminal:
      // setting run=true after them via usePropSync can silently fail in
      // production once the store is stuck. A new key gives STATUS.READY.
      setTourKey(k => k + 1);
      navigate(ROUTES.cockpit);
      // [data-tour="sidebar-toggle"] lives in the AppBar (always in DOM), so
      // no polling needed — just wait for the navigation frame to settle.
      setTimeout(() => {
        setStepIndex(0);
        setRun(true);
      }, 600);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFinish = useCallback(() => {
    localStorage.setItem(TOUR_KEY, 'true');
    setShowClosing(false);
    setTourActive(false);
    navigate(ROUTES.cockpit);
  }, [navigate]);

  const handleCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    if (status === STATUS.FINISHED) {
      setRun(false);
      setShowClosing(true);
      // tourActive stays true — the closing modal is still up next,
      // background scrolling should stay locked until handleFinish dismisses it.
      return;
    }
    if (status === STATUS.SKIPPED) {
      localStorage.setItem(TOUR_KEY, 'true');
      setRun(false);
      setTourActive(false);
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Handles everything beyond the sidebar-open/close special cases below:
      // page navigation (NAV_BEFORE_STEP) or a plain same-page scroll+switch.
      const advanceTo = (idx) => {
        // Going back, return to the previous step's own page if it's a
        // different one than the page currently showing.
        const navConfig = action === ACTIONS.PREV
          ? (window.location.pathname !== routeForStep(idx) && typeof steps[idx]?.target === 'string'
              ? { route: routeForStep(idx), target: steps[idx].target }
              : null)
          : NAV_BEFORE_STEP[idx];

        if (navConfig) {
          // Navigate to another page, then poll for the step's target element
          setRun(false);
          navigate(navConfig.route);
          let tries = 0;
          const poll = () => {
            if (document.querySelector(navConfig.target) || tries++ >= 30) {
              // Slow network/device may still be loading the target page's data after
              // the timeout — proceed anyway rather than leaving the tour stuck with no
              // tooltip; Joyride's own TARGET_NOT_FOUND handling is the fallback net.
              const headerOffset = steps[idx]?.scrollHeaderOffset ?? getHeaderOffset();
              scrollTargetIntoView(navConfig.target, headerOffset, isMobile);
              if (isMobile) fitStepAndTooltipInViewport(navConfig.target, headerOffset);
              setStepIndex(idx);
              setRun(true);
            } else {
              setTimeout(poll, 300);
            }
          };
          setTimeout(poll, 500);
          return;
        }

        // Same-page advance — scroll the next step's target into view ourselves
        // (see scrollTargetIntoView above), then switch; floating-ui keeps the
        // tooltip glued to the target as the scroll animates in.
        const nextStep = steps[idx];
        if (typeof nextStep?.target === 'string') {
          scrollTargetIntoView(nextStep.target, nextStep.scrollHeaderOffset ?? 90, isMobile);
          if (isMobile) fitStepAndTooltipInViewport(nextStep.target, nextStep.scrollHeaderOffset ?? 90);
        }
        setStepIndex(idx);
      };

      // v3 controlled mode: stepping past the last step doesn't auto-emit STATUS.FINISHED
      if (nextIndex >= steps.length && action !== ACTIONS.PREV) {
        setRun(false);
        setShowClosing(true);
        return;
      }

      // Going TO step 1: open sidebar, then poll for list element. Scroll the
      // page to the very top first — the drawer/nav list starts at the top of
      // the screen, so any leftover scroll position from step 0 could leave
      // its beginning hidden behind the header.
      if (nextIndex === 1) {
        setRun(false);
        window.scrollTo(0, 0);
        window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: { open: true } }));
        let tries = 0;
        const poll = () => {
          const el = document.querySelector('[data-tour="sidebar-list"]');
          if ((el && el.getBoundingClientRect().left >= 0) || tries++ >= 20) {
            // On a slow device the drawer animation may still be mid-flight after the
            // timeout — proceed anyway rather than leaving the tour stuck with no
            // tooltip; Joyride's own TARGET_NOT_FOUND handling is the fallback net.
            setStepIndex(1);
            setRun(true);
          } else {
            setTimeout(poll, 200);
          }
        };
        setTimeout(poll, 350);
        return;
      }

      // Leaving step 1: close sidebar first, then advance
      if (index === 1) {
        window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: { open: false } }));
        setTimeout(() => { advanceTo(nextIndex); }, 300);
        return;
      }

      // Step 6 (nav-addons) targets an element that lives inside the same
      // sidebar drawer as step 1 — reopen it on the way in, close it again
      // on the way out, same pattern as step 1 above.
      const enteringSidebarZone = nextIndex === 6 && index !== 6;
      const leavingSidebarZone  = index === 6 && nextIndex !== 6;

      if (enteringSidebarZone) {
        setRun(false);
        window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: { open: true } }));
        let tries = 0;
        const poll = () => {
          const el = document.querySelector(steps[nextIndex].target);
          if ((el && el.getBoundingClientRect().left >= 0) || tries++ >= 20) {
            setStepIndex(nextIndex);
            setRun(true);
          } else {
            setTimeout(poll, 200);
          }
        };
        setTimeout(poll, 350);
        return;
      }

      if (leavingSidebarZone) {
        window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: { open: false } }));
        setTimeout(() => { advanceTo(nextIndex); }, 300);
        return;
      }

      advanceTo(nextIndex);
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Joyride
        key={tourKey}
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        onEvent={handleCallback}
        beaconComponent={AutoTriggerBeacon}
        locale={{
          back:             t('tour_btn_back'),
          close:            t('tour_btn_close'),
          last:             t('tour_btn_last'),
          next:             t('tour_btn_next'),
          nextWithProgress: t('tour_btn_next_with_progress'),
          open:             t('tour_btn_open'),
        }}
        options={{
          // Without this, the X button just advances to the next step (react-joyride's
          // default 'close' action) instead of actually exiting the tour — 'skip' makes
          // it call the same control the Skip button uses, ending the tour for real.
          closeButtonAction: 'skip',
          primaryColor:    accent,
          zIndex:          9000,
          // p.background.paper defaults to MUI's stock dark value (#121212),
          // which is darker than the page's own background.default — the
          // tooltip was disappearing into the dimmed overlay instead of
          // popping forward. greydark.C800 sits a clear step lighter than
          // the page background so it actually reads as a raised surface.
          backgroundColor: dark ? p.greydark.C800 : p.background.paper,
          arrowColor:      dark ? p.greydark.C800 : p.background.paper,
          // Darker on mobile — a stronger dim on the rest of the screen makes the
          // (smaller, more cramped) spotlighted area read as clearly the one thing
          // in focus rather than getting lost against the surrounding content.
          overlayColor:    alpha(p.common.black, isMobile ? 0.62 : 0.52),
          spotlightPadding: isMobile ? 6 : 10,
          textColor:       p.text.primary,
          width:           isMobile ? 'calc(100vw - 40px)' : 390,
          // Joyride's own scroll-into-view doesn't work in this app (see
          // scrollTargetIntoView near the top of this file) — every step advance
          // scrolls manually instead, so its built-in attempt is disabled here.
          skipScroll:      true,
          showProgress:    true,
          buttons:         ['back', 'close', 'primary'],
        }}
        styles={{
          tooltip: {
            borderRadius: 12,
            padding:      isMobile ? '26px 22px' : '28px 32px',
            width:        isMobile ? 'calc(100vw - 40px)' : 390,
            maxWidth:     isMobile ? 'calc(100vw - 40px)' : 390,
            boxShadow: dark
              ? `0 16px 48px ${alpha(p.common.black, 0.5)}`
              : `0 16px 48px ${alpha(p.common.black, 0.14)}`,
          },
          tooltipTitle: {
            fontSize:     '0.9rem',
            fontWeight:   700,
            marginBottom: 10,
          },
          tooltipContent: {
            padding:   '10px 0 6px',
            textAlign: 'left',
          },
          tooltipFooter: {
            marginTop: 20,
          },
          buttonClose: {
            top:   isMobile ? 14 : 8,
            right: isMobile ? 14 : 8,
          },
          buttonPrimary: {
            backgroundColor: dark ? p.grey.C200    : p.greydark.C900,
            color:           dark ? p.common.black : p.common.white,
            borderRadius:    '20px',
            border:          'none',
            fontSize:        '0.85rem',
            fontWeight:      600,
            padding:         '10px 20px',
          },
          buttonBack: {
            color:       p.text.secondary,
            fontSize:    '0.85rem',
            marginRight: 8,
          },
          beacon:      { width: 0, height: 0, opacity: 0, pointerEvents: 'none' },
          beaconInner: { width: 0, height: 0, opacity: 0 },
          beaconOuter: { width: 0, height: 0, opacity: 0 },
          // The cut-out itself was just a plain transparent hole before — give it a
          // colored ring so the highlighted area reads as "this, in color" against
          // the dimmed rest of the page, instead of just an ordinary lit patch.
          spotlight: {
            borderRadius: isMobile ? 10 : 8,
            border: `2px solid ${accent}`,
            boxShadow: `0 0 0 4px ${alpha(accent, 0.22)}, 0 0 24px 2px ${alpha(accent, 0.35)}`,
          },
        }}
      />

      {showClosing && <ClosingModal onFinish={handleFinish} />}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   RESTART BUTTON  — drop anywhere inside <TourProvider>
───────────────────────────────────────────────────────────────────────────── */

export function TourRestartButton() {
  const { restart } = useTour();
  const { t } = useTranslation();
  const theme = useTheme();
  const color = theme.palette.mode === 'dark'
    ? theme.palette.greydark.C300
    : theme.palette.greydark.C900;

  return (
    <Box
      component="button"
      onClick={restart}
      title={t('tour_restart_label')}
      aria-label={t('tour_restart_label')}
      sx={{
        background: 'none',
        border: `2px solid ${color}`,
        borderRadius: '50%',
        width: 42, height: 42,
        color,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        p: 0,
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: alpha(color, 0.1) },
      }}
    >
      <HelpCircle size={22} />
    </Box>
  );
}
